import { clsx } from "clsx";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { FaAngleDown } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import { useSettings } from "../../context/SettingsContext";
import { useLanguageService } from "../../hooks";
import { postLanguageChangeMessage } from "../../utils/message.helper";
import { publicAssetUrl } from "../../config";

interface LanguageOption {
  code: string;
  name: string;
  url: string;
}
export interface LanguageDropdownProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedLanguage: LanguageOption | null;
  languages: LanguageOption[];
  className?: string;
}

const LanguageSelector = forwardRef<HTMLDivElement, LanguageDropdownProps>(
  ({ languages, selectedLanguage, isOpen, setIsOpen, className = "" }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { widgetDataSettings, setSelectedLanguage } = useSettings();
    const { handleSaveData } = useLanguageService();

    const onToggle = useCallback(() => {
      setIsOpen(!isOpen);
    }, [isOpen, setIsOpen]);

    const onLanguageSelect = async (language: any) => {
      setSelectedLanguage(language);
      setIsOpen(false);
      await handleSaveData(language.code);
      postLanguageChangeMessage(language.code);
    };

    // Click outside handler for dropdown
    useEffect(() => {
      const handleClickOutsideDropdown = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutsideDropdown);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutsideDropdown);
      };
    }, [isOpen, setIsOpen]);

    return (
      <div
        ref={(node) => {
          // Assign to both the internal containerRef and the forwarded ref
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }}
        className={`relative min:w-[12rem] top-0 w-auto ${className}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
      >
        <div
          className="flex items-center cursor-pointer float-right py-1 rounded-none sm:rounded-lg"
          style={{
            background: widgetDataSettings?.settings?.appearance?.colors
              ?.buttonColor
              ? widgetDataSettings.settings.appearance.colors
                  .buttonGradientEnabled
                ? `linear-gradient(${widgetDataSettings.settings.appearance.colors.buttonColorGradient}, ${widgetDataSettings.settings.appearance.colors.buttonColor})`
                : widgetDataSettings.settings.appearance.colors.buttonColor
              : "#0048FF",
            borderRadius: "20px",
          }}
          onClick={onToggle}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <img
              src={selectedLanguage?.url || publicAssetUrl("english.png")}
              className="w-4 h-4"
              alt={selectedLanguage?.name || "Default Language"}
            />
          </div>
          <p
            className={`text-[16px] ${
              widgetDataSettings?.settings?.advanced?.oversizedWidget ? "font-semibold" : ""
            } text-white ml-2 truncate max-w-[120px]`}
          >
            {selectedLanguage?.name}
          </p>
          <FaAngleDown color="white" />
        </div>
        {isOpen && (
          <div
            ref={dropdownRef}
            className={twMerge(
              clsx(
                "absolute top-full mt-9 border rounded-lg bg-white shadow-lg z-40",
                {
                  "right-[-14px] w-[14rem]":
                    Array.isArray(languages) &&
                    (languages ?? []).some(
                      (lang) => lang.name && lang.name.length > 15
                    ),
                  "right-[-16px] w-[13rem]":
                    !Array.isArray(languages) ||
                    !(languages ?? []).some(
                      (lang) => lang.name && lang.name.length > 15
                    ),
                }
              )
            )}
          >
            <ul
              className="max-h-60 overflow-y-auto w-full"
              style={{ overflowX: "hidden", paddingBottom: "4px" }}
              tabIndex={-1}
            >
              {languages.map((language) => (
                <li
                  key={language.code}
                  className="flex items-center p-2 cursor-pointer hover:bg-gray-100 truncate"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLanguageSelect(language);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onLanguageSelect(language);
                    }
                  }}
                  tabIndex={0}
                  style={{
                    background:
                      selectedLanguage?.code === language.code
                        ? (widgetDataSettings?.settings?.appearance?.colors
                            ?.buttonGradientEnabled
                            ? `linear-gradient(${
                                widgetDataSettings.settings.appearance.colors
                                  .buttonColorGradient || "#000"
                              }, ${
                                widgetDataSettings.settings.appearance.colors
                                  .buttonColor || "#0048FF"
                              })`
                            : widgetDataSettings.settings.appearance.colors
                                .buttonColor)
                        : "transparent",
                    color:
                      selectedLanguage?.code === language.code
                        ? "#FFFFFF" : "black",
                  }}
                  title={language.name}
                >
                  <img
                    src={language.url}
                    alt={language.name}
                    className="w-5 h-5 mr-2 flex-shrink-0"
                  />
                  <span className="truncate">{language.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

LanguageSelector.displayName = "LanguageSelector";

export default LanguageSelector;
